/**
 * Group breakdown for the onboarding journey. Mirrors Sparta's
 * `OnboardingStepGroups` shape (sparta/src/onboarding/constants/onboardingStepGroups.ts) —
 * each group is one progress milestone, so the bar advances when the user
 * crosses a group boundary, not on every step.
 *
 * Per-step `showHeader` / `showProgress` / `showBack` flags let the config
 * own visibility decisions instead of pushing them onto every consumer
 * (in Sparta the header container had to hardcode the AccountCreation /
 * TransitionToNativeApp exception list).
 */

export type OnboardingStepGroupDirection = 'forward' | 'bi-directional';

export type OnboardingStep = {
  /** Expo pathname for the step, e.g. '/step/company-search'. */
  path: string;
  /**
   * Default true. False removes the persistent OnboardingHeader entirely on
   * this step — implies both `showProgress: false` and `showBack: false`.
   */
  showHeader?: boolean;
  /** Default true. False hides the progress bar within an otherwise visible header. */
  showProgress?: boolean;
  /** Default true. False hides the back chevron within an otherwise visible header. */
  showBack?: boolean;
  /**
   * Default false. True pins the progress bar to 100% on this step regardless of
   * its group position — for a terminal dead end (e.g. empty-program) where the
   * journey can't continue, so a partial bar would read as "more to do".
   */
  progressComplete?: boolean;
};

export type OnboardingStepGroup = {
  steps: OnboardingStep[];
  /** Default: 'forward'. Only 'bi-directional' groups allow back navigation. */
  direction?: OnboardingStepGroupDirection;
};

export const ONBOARDING_STEP_GROUPS: OnboardingStepGroup[] = [
  // Step: PURL DOB VERIFICATION — the dedicated pre-account entry screen for
  // PURL ("Personalized URL") members (welcome + confirm date of birth). Like
  // account creation it runs before a session exists, so no header/progress.
  {
    steps: [{ path: '/step/purl-dob-verification', showHeader: false }],
    direction: 'forward',
  },
  // Step: CREATE ACCOUNT
  {
    steps: [{ path: '/step/account-creation', showHeader: false }],
    direction: 'forward',
  },
  // Step: ELIGIBILITY CHECK
  {
    steps: [
      { path: '/step/company-search' },
      { path: '/step/employer-or-health-plan-verification' },
      { path: '/step/employee-zip-verification' },
    ],
    direction: 'bi-directional',
  },
  // Step: ELIGIBILITY RESUBMIT — forward-only so back doesn't dump the user
  // into a screen whose draft state is already invalid for retry.
  {
    steps: [{ path: '/step/employee-search' }],
    direction: 'forward',
  },
  // Step: ELIGIBILITY RESULT — success (eligible) or terminal failure
  // (not-eligible). Mirrors Sparta's forward-only `[NotEligible, Eligible]`
  // group, while keeping onboarding's persistent header visible.
  {
    steps: [{ path: '/step/eligible' }, { path: '/step/not-eligible' }],
    direction: 'forward',
  },
  // Step: CREATE PROFILE
  {
    steps: [{ path: '/step/create-profile' }],
    direction: 'forward',
  },
  // Step: STATIC SURVEY — health conditions + goals (mirrors Sparta's STATIC
  // SURVEY group). Bi-directional so back from /step/health-goals returns to
  // /step/health-conditions, never out of the group. health-conditions is the
  // group's first step, so it shows no back chevron. LEGACY (control) arm only:
  // in the `onb_use_dynamic_assessment` test arm the resolver routes the whole
  // survey region to /step/assigned-screeners (conditions + goals run there as
  // dynamic flows) and these steps are never visited.
  {
    steps: [{ path: '/step/health-conditions' }, { path: '/step/health-goals' }],
    direction: 'bi-directional',
  },
  // Step: DYNAMIC SURVEY — backend-assigned screeners, then state-compliance,
  // then the org-gated pre-diabetes / phq-gad screeners. Forward-only. Order
  // mirrors Sparta's chain (screeners → state-compliance → pre-diabetes → phq-gad;
  // see setNextStepOfStateComplianceDisclaimersEpic). The `onb_use_dynamic_assessment`
  // experiment picks the survey ENTRY: control → the legacy screeners at
  // /step/assigned-screeners (reached via the linear walk from health-goals);
  // test → the dynamic-assessment flows at /step/dynamic-assessment for the WHOLE
  // survey region (health conditions → goals → AOM → diabetes decision →
  // pre-diabetes → phq/gad),
  // then the legacy flow for the unmigrated screeners at /step/assigned-screeners.
  //
  // `/step/dynamic-assessment` is deliberately NOT in this array: it's a test-arm
  // entry the resolver routes to directly (test-arm forward nav then uses
  // `dynamicSurveyNextStep`, not the linear group order), and adding it would
  // corrupt `findNextStep`'s group-boundary crossings (health-goals → the group's
  // first step, and phq-gad → the program region). It's aliased to this group for
  // progress/chrome in `getStepContext` instead.
  //
  // pre-diabetes and phq-gad are conditional: skipped when the org doesn't require
  // them (preDiabetesRequired / phqGadRequired) AND when the dynamic pass already
  // served them — forward nav skips via `dynamicSurveyNextStep` rather than the
  // linear group order.
  {
    steps: [
      { path: '/step/assigned-screeners' },
      { path: '/step/state-compliance-disclaimer-page' },
      { path: '/step/pre-diabetes-screen' },
      { path: '/step/phq-gad-questionnaire' },
    ],
    direction: 'forward',
  },
  // Step: SELECT PROGRAM — program matching list / primary-recommendation
  // detail → optional coaching method → review (the enroll happens on review).
  // Forward-only; intra-flow branching is selection-driven (programMatchingNextStep)
  // rather than the linear group order, and "See other options" handles
  // returning to the list. `crisis-program-selection` (PHQ/GAD crisis-line
  // result) and `empty-program` (no matching program) are alternate entries the
  // resolver routes to (Sparta groups both here too). Back is hidden — they're
  // forward-only, not list picks; `empty-program` keeps the header visible and
  // pins the progress bar to 100% (`progressComplete`) since it's a terminal
  // dead end. Their position in the group is inert: every program-region advance
  // is selection/enroll/resolver-driven, not findNextStep's linear order.
  {
    steps: [
      { path: '/step/program-matching' },
      { path: '/step/primary-recommendation-program-detail-screen' },
      { path: '/step/coaching-method-screen' },
      { path: '/step/full-choice-program-review-screen' },
      { path: '/step/crisis-program-selection', showBack: false },
      { path: '/step/empty-program', showBack: false, progressComplete: true },
    ],
    direction: 'forward',
  },
  // Step: TESTIMONIAL — optional, between program enrollment and coach matching
  // (Sparta's standalone TestimonialPage group). Reached only when the resolver
  // sees `showTestimonialPage`; its Continue advances to coach matching, so
  // there's nothing to go back to — forward-only, no back chevron.
  {
    steps: [
      { path: '/step/testimonial-page', showBack: false, showHeader: true, showProgress: false },
    ],
    direction: 'forward',
  },
  // Step: SELECT COACH — reached once the user has a program instance. The
  // intra-phase advance is imperative (Continue handlers). Bi-directional so the
  // user can back out of /step/coach-availability to /step/coach-matching and
  // re-pick a coach (no coach is assigned until Schedule/Skip on availability).
  // coach-matching is the group entry (stepIndex 0 → no back chevron; its
  // predecessor is the forward-only program group). The post-assignment steps
  // (success, intro message) stay forward-only — by then `assignPrimaryCoach`
  // has run, so back would drop into a stale availability picker — hence
  // showBack: false on each.
  //
  // INTENTIONAL Sparta divergence: Sparta splits these into separate progress
  // groups (so the bar advances per screen); we keep one group — the four coach
  // screens are a single conceptual "find your coach" phase and the bi-directional
  // back above depends on matching + availability sharing a group. Net effect: the
  // progress bar holds steady across the coach phase here. Deliberate, not a gap.
  {
    steps: [
      { path: '/step/coach-matching' },
      { path: '/step/coach-availability' },
      { path: '/step/coach-matching-success', showBack: false },
      { path: '/step/quick-intro-messages', showBack: false },
    ],
    direction: 'bi-directional',
  },
  // Step: MEMBER CONTACT — optional address + phone collection (ME-1256),
  // gated by the member-contact experiment split. Reached after the coach phase
  // ("after provider scheduling"), before the terminal. Its own forward-only
  // group rather than wedged into the bi-directional coach group (which would
  // break the matching↔availability selected-coach handoff). Forward-only: a
  // coach is already assigned by now, so there's nothing to go back to; Continue
  // and Skip both advance to the terminal.
  {
    steps: [{ path: '/step/member-contact', showBack: false }],
    direction: 'forward',
  },
  // Step: TRANSITION TO NATIVE APP — terminal, reached once the user has a
  // primary coach.
  {
    steps: [
      {
        path: '/step/transition-to-native-app',
        showProgress: false,
        showBack: false,
        showHeader: false,
      },
    ],
    direction: 'forward',
  },
];
